import { defaultModelFor } from '@shared/ai/models';
import { aiRequestInit, aiTarget, isServerRefusal } from './AiGateway';
import { formatSize, type ImageSize, type RenderQualityTier } from './ImageSpec';

/**
 * Client for OpenAI's image edit endpoint, used to re-render a captured frame in a chosen style.
 *
 * `gpt-image-2` is the model of record here for two reasons: it accepts multiple reference images —
 * which is what lets the species ID map and depth map travel alongside the beauty render — and it
 * processes every image input at high fidelity automatically, so there is no `input_fidelity` knob
 * to get wrong.
 *
 * Image order is load-bearing. The endpoint exposes uploads to the prompt as "image 1", "image 2",
 * "image 3" in the order they are appended, and `PromptBuilder` describes them in exactly that
 * order, so {@link ENHANCE_IMAGE_ORDER} must stay in sync with the prompt's input legend.
 */

/**
 * Where this request goes is resolved per call by `AiGateway`: straight to OpenAI with the user's
 * own key in the single-user build, or to this origin's `/api/ai/enhance` on a hosted deployment,
 * where the server attaches a key the browser never holds. The body below is identical either way.
 */

export const ENHANCE_MODEL = defaultModelFor('enhance');

/** Positional meaning of each uploaded image, as described to the model in the prompt. */
export const ENHANCE_IMAGE_ORDER = ['beauty', 'idMap', 'depth'] as const;

/** Maximum variations the endpoint will return in one request. */
export const MAX_VARIATIONS = 10;

export interface EnhanceRequest {
  apiKey: string;
  /**
   * Beauty render first, then the ID map, then the depth map, then any designer-supplied reference
   * photographs. Everything past the third slot is uploaded as `reference-N` and is only meaningful
   * if the prompt numbers it — see `PromptBuilder`'s `referenceCount`.
   */
  images: readonly Blob[];
  prompt: string;
  size: ImageSize;
  quality: RenderQualityTier;
  /** Number of variations, 1–10. */
  count?: number;
  signal?: AbortSignal;
  /** Injected for tests. Defaults to the global `fetch`. */
  fetchImpl?: typeof fetch;
}

export interface EnhanceResult {
  images: Blob[];
  model: string;
}

/** An API failure translated into something a user can act on. */
export class EnhanceError extends Error {
  constructor(
    message: string,
    readonly status: number | null,
    readonly retryable: boolean,
  ) {
    super(message);
    this.name = 'EnhanceError';
  }
}

export async function enhanceRender(request: EnhanceRequest): Promise<EnhanceResult> {
  const doFetch = request.fetchImpl ?? globalThis.fetch;
  if (typeof doFetch !== 'function') {
    throw new EnhanceError('No fetch implementation is available in this environment.', null, false);
  }
  if (request.images.length === 0) {
    throw new EnhanceError('At least one image is required to enhance a render.', null, false);
  }

  const form = new FormData();
  form.append('model', ENHANCE_MODEL);
  form.append('prompt', request.prompt);
  form.append('size', formatSize(request.size));
  form.append('quality', request.quality);
  form.append('output_format', 'png');
  form.append('n', String(clampCount(request.count ?? 1)));

  // `image[]` repeated — the endpoint reads position as "image 1", "image 2", …
  request.images.forEach((blob, index) => {
    const name = ENHANCE_IMAGE_ORDER[index] ?? `reference-${index + 1}`;
    form.append('image[]', blob, `${name}.png`);
  });

  const target = aiTarget('enhance', request.apiKey);
  let response: Response;
  try {
    response = await doFetch(target.url, {
      method: 'POST',
      ...aiRequestInit(target),
      body: form,
      ...(request.signal ? { signal: request.signal } : {}),
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new EnhanceError(
      'Could not reach the OpenAI API. Check your network connection.',
      null,
      true,
    );
  }

  if (!response.ok) {
    throw await describeFailure(response);
  }

  const payload = (await response.json()) as {
    data?: { b64_json?: string; url?: string }[];
  };
  const entries = payload.data ?? [];
  if (entries.length === 0) {
    throw new EnhanceError('The API returned no image.', response.status, true);
  }

  const images: Blob[] = [];
  for (const entry of entries) {
    if (typeof entry.b64_json !== 'string') continue;
    images.push(base64ToPngBlob(entry.b64_json));
  }
  if (images.length === 0) {
    throw new EnhanceError('The API response contained no decodable image data.', response.status, true);
  }

  return { images, model: ENHANCE_MODEL };
}

function clampCount(count: number): number {
  if (!Number.isFinite(count)) return 1;
  return Math.min(MAX_VARIATIONS, Math.max(1, Math.trunc(count)));
}

/**
 * Turn an HTTP failure into a message that names the fix. The raw API error is appended when it adds
 * something — a bare "400 Bad Request" in a toast helps nobody.
 */
async function describeFailure(response: Response): Promise<EnhanceError> {
  const { detail, code } = await readErrorDetail(response);

  // A refusal this app's own server wrote a sentence for — out of credits, over the cost cap,
  // suspended, AI switched off. It already names the state and the way out, and every branch below
  // would replace it with something wrong: on a hosted deployment the user has no OpenAI key whose
  // permissions to check and no billing page to visit.
  if (isServerRefusal(code) && detail !== null) {
    return new EnhanceError(detail, response.status, false);
  }

  switch (response.status) {
    case 401:
      return new EnhanceError(
        'OpenAI rejected the API key. Check that it is correct and still active.',
        401,
        false,
      );
    case 403:
      return new EnhanceError(
        'This API key is not permitted to use image generation. Verify the key\'s project has image models enabled.',
        403,
        false,
      );
    case 429:
      return new EnhanceError(
        'Rate limited or out of quota. Wait a moment and retry, or check your billing at platform.openai.com.',
        429,
        true,
      );
    case 400:
      return new EnhanceError(
        detail ?? 'The request was rejected. The capture size or prompt may be outside the supported range.',
        400,
        false,
      );
    default:
      break;
  }

  if (response.status >= 500) {
    return new EnhanceError('OpenAI had a server error. Retrying usually works.', response.status, true);
  }
  return new EnhanceError(detail ?? `Request failed (HTTP ${response.status}).`, response.status, false);
}

/**
 * The message and the machine-readable reason out of an error body.
 *
 * One shape covers both upstreams by luck rather than design: OpenAI answers `{ error: { message,
 * code } }` and so does this app's own proxy. The `code` is what lets a refusal be told apart from
 * a rate limit, which a status of 429 alone cannot do.
 */
async function readErrorDetail(response: Response): Promise<{
  detail: string | null;
  code: string | null;
}> {
  try {
    const body = (await response.json()) as { error?: { message?: string; code?: string } };
    const message = body.error?.message;
    const code = typeof body.error?.code === 'string' ? body.error.code : null;
    if (typeof message !== 'string' || message.length === 0) return { detail: null, code };
    if (code === 'moderation_blocked' || /safety|moderation/i.test(message)) {
      return { detail: `The content filter rejected this render: ${message}`, code };
    }
    return { detail: message, code };
  } catch {
    return { detail: null, code: null };
  }
}

/** Decode the endpoint's base64 payload into a PNG blob. */
export function base64ToPngBlob(base64: string): Blob {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: 'image/png' });
}
